import type { NextAuthOptions } from 'next-auth';
import Credentials from 'next-auth/providers/credentials';

import { buildDirectBackendUrl, DIRECT_BACKEND_API_URL } from './direct-backend-api';

const API_BASE_URL = DIRECT_BACKEND_API_URL;
const PAYLOAD_SERVICE_KEY = process.env.NEXT_PAYLOAD_SERVICE_KEY || '';

interface AuthorizedUserResponse {
  user?: {
    id: string | number;
    email?: string;
  };
}

async function fetchAuthorizedUser(accessToken?: string): Promise<{
  id: string | number;
  email?: string;
}> {
  const response = await fetch(buildDirectBackendUrl('/api/users/me'), {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    cache: 'no-store',
  });
  const data = (await response.json().catch(() => ({}))) as AuthorizedUserResponse;
  if (!response.ok || !data.user) {
    throw new Error('Failed to fetch authorized user');
  }
  return data.user;
}

const handleApiError = (error: unknown) => {
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : error && typeof error === 'object' && 'message' in error
          ? String((error as { message: unknown }).message)
          : '';

  try {
    const parsed = JSON.parse(message);
    if (parsed?.code === 'EMAIL_EXISTS') return 'Email занят';
    if (parsed?.code === 'LOGIN_EXISTS') return 'Логин занят';
  } catch {
    // не JSON — идём дальше по тексту
  }

  if (message.includes('EMAIL_EXISTS')) {
    return 'Email занят';
  }
  if (message.includes('LOGIN_EXISTS')) {
    return 'Логин занят';
  }

  if (
    message.includes('User with this email already exists') ||
    message.toLowerCase().includes('email already exists')
  ) {
    return 'Email занят';
  }

  if (
    message.toLowerCase().includes('name already exists') ||
    message.toLowerCase().includes('username already exists') ||
    message.toLowerCase().includes('login already exists') ||
    message.toLowerCase().includes('user with this name already exists')
  ) {
    return 'Логин занят';
  }

  if (message.includes('email')) {
    return 'Неверный email';
  }
  if (message.includes('password')) {
    return 'Неверный пароль';
  }
  if (message.includes('already exists')) {
    return 'Email занят';
  }
  if (message.includes('not found')) {
    return 'Пользователь не найден';
  }
  if (message.includes('invalid')) {
    return 'Неверные данные для входа';
  }
  if (message.includes('Not passed')) {
    return 'Неверные данные для входа';
  }
  return message || 'Произошла ошибка при авторизации';
};

const apiVersion = '5.199';

export const authOptions: NextAuthOptions = {
  providers: [
    {
      id: 'vk',
      name: 'VKontakte',
      type: 'oauth',
      clientId: process.env.VK_CLIENT_ID || '',
      clientSecret: process.env.VK_CLIENT_SECRET || '',
      authorization: {
        url: 'https://oauth.vk.com/authorize',
        params: {
          scope: 'email',
          response_type: 'code',
          v: apiVersion,
        },
      },
      token: {
        url: 'https://oauth.vk.com/access_token',
        params: {
          v: apiVersion,
        },
      },
      userinfo: {
        async request({ tokens }) {
          const response = await fetch(
            `https://api.vk.com/method/users.get?access_token=${tokens.access_token}&fields=photo_100,email&v=${apiVersion}`,
          );
          const data = await response.json();
          console.log('VK userinfo response:', data);
          return data.response?.[0] || {};
        },
      },
      profile(profile) {
        console.log('=== VK PROFILE CALLBACK START ===');
        console.log('Raw VK profile data:', profile);
        console.log('Profile fields:', {
          id: profile.id,
          first_name: profile.first_name,
          last_name: profile.last_name,
          email: profile.email,
          photo_100: profile.photo_100,
          screen_name: profile.screen_name,
        });

        const processedProfile = {
          id: profile.id,
          name: `${profile.first_name} ${profile.last_name}`,
          email: profile.email || `vk_${profile.id}@vk.com`,
          image: profile.photo_100,
        };

        console.log('Processed profile:', processedProfile);
        console.log('=== VK PROFILE CALLBACK END ===');

        return processedProfile;
      },
    },
    // YandexProvider убран - используем custom callback handler
    // YandexProvider({
    //   clientId: process.env.YANDEX_CLIENT_ID || '',
    //   clientSecret: process.env.YANDEX_CLIENT_SECRET || '',
    //   authorization: {
    //     params: {
    //       scope: 'login:email login:avatar',
    //     },
    //   },
    // }),
    Credentials({
      name: 'credentials',
      credentials: {
        email: { label: 'Email', type: 'email' },
        password: { label: 'Password', type: 'password' },
        name: { label: 'Name', type: 'text' },
        action: { label: 'Action', type: 'text' }, // "login" или "register"
        ref: { label: 'Ref', type: 'text' },
        accessToken: { label: 'Access Token', type: 'text' }, // Для VK авторизации
      },
      async authorize(credentials) {
        if (!credentials?.email || !credentials?.password) {
          return null;
        }

        try {
          // Проверяем, является ли это VK авторизацией
          if (credentials.action === 'login' && credentials.password.startsWith('vk_')) {
            console.log('=== VK CREDENTIALS AUTH ===');
            console.log('VK user login attempt:', credentials.email);

            const userResponse = await fetchAuthorizedUser(credentials.accessToken);

            console.log('userResponse', userResponse, credentials.accessToken);

            // Для VK пользователей просто возвращаем данные без проверки пароля
            return {
              id: userResponse.id,
              email: userResponse.email,
              name: credentials.name || credentials.email.split('@')[0],
              accessToken: credentials.accessToken || 'vk_token',
            };
          }

          // Проверяем, является ли это Yandex авторизацией
          if (credentials.action === 'login' && credentials.password.startsWith('yandex_')) {
            console.log('=== YANDEX CREDENTIALS AUTH ===');
            console.log('Yandex user login attempt:', credentials.email);

            const userResponse = await fetchAuthorizedUser(credentials.accessToken);

            console.log('userResponse', userResponse, credentials.accessToken);

            // Для Yandex пользователей просто возвращаем данные без проверки пароля
            return {
              id: userResponse.id,
              email: userResponse.email,
              name: credentials.name || credentials.email.split('@')[0],
              accessToken: credentials.accessToken || 'yandex_token',
            };
          }
          if (credentials.action === 'register') {
            // Регистрация
            const registerResponse = await fetch(`${API_BASE_URL}/api/users/register`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: credentials.email,
                password: credentials.password,
                name: credentials.name || credentials.email.split('@')[0],
                ...(credentials.ref && { ref: credentials.ref }),
              }),
            });

            if (!registerResponse.ok) {
              const error = await registerResponse.json();
              console.log('registerData', error);
              throw new Error(error?.error || 'Registration failed');
            }

            const loginResponse = await fetch(`${API_BASE_URL}/api/users/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: credentials.email,
                password: credentials.password,
              }),
            });

            if (!loginResponse.ok) {
              throw new Error('Auto-login after registration failed');
            }

            const loginData = await loginResponse.json();

            return {
              id: loginData.user.id || loginData.user.email,
              email: loginData.user.email,
              name: loginData.user.name,
              accessToken: loginData.token,
            };
          } else {
            // Вход
            const loginResponse = await fetch(`${API_BASE_URL}/api/users/login`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                email: credentials.email,
                password: credentials.password,
              }),
            });

            if (!loginResponse.ok) {
              const error = await loginResponse.json();
              console.log('error');
              throw new Error(JSON.stringify(error) || 'Login failed');
            }

            const loginData = await loginResponse.json();
            console.log('loginData', loginData);
            return {
              id: loginData.user.id || loginData.user.email,
              email: loginData.user.email,
              name: loginData.user.name,
              accessToken: loginData.token,
              donationUserId: loginData.user.donationUserId,
            };
          }
        } catch (error: unknown) {
          console.error('Auth error:', error);

          throw new Error(handleApiError(error), { cause: error });
        }
      },
    }),
  ],
  callbacks: {
    async jwt({ token, user }) {
      if (user) {
        token.accessToken = user.accessToken;
        token.id = user.id;
        token.isNewUser = (user as any).isNewUser || false;
      }
      return token;
    },
    async session({ session, token }) {
      if (token) {
        session.user.id = Number(token.id);
        session.accessToken = token.accessToken as string;
        (session as any).isNewUser = token.isNewUser as boolean;
      }
      return session;
    },
    async signIn({ user, account, profile }: any) {
      console.log('=== SIGNIN CALLBACK START ===');
      console.log('Provider:', account?.provider);
      console.log('User:', user);
      console.log('Account:', account);
      console.log('Profile:', profile);
      console.log('=== SIGNIN CALLBACK END ===');

      if (
        account?.provider === 'vk'
        // || (user.id.toString().startsWith('vk_') && user.id.includes('@vk.com'))
      ) {
        try {
          console.log('=== VK LOGIN PROCESSING START ===');
          console.log('VK Profile data:', profile);
          console.log('VK User data:', user);
          console.log('VK Account data:', account);

          // Получаем данные из нашего кастомного провайдера
          const email = user.email || profile?.email;
          const vkId = user.id?.toString() || profile?.id?.toString();

          console.log('Extracted data:', {
            email,
            vkId,
            name: user.name,
            avatar: user.image,
          });

          if (!email || !vkId) {
            console.error('VK: Missing email or vkId:', { email, vkId });
            return false;
          }

          console.log('VK service-login request:', {
            vkId,
            email,
            name: user.name,
            avatar: user.image,
          });

          // Запрос к Payload CMS
          const res = await fetch(`${API_BASE_URL}/api/users/service-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              SERVICE_KEY: PAYLOAD_SERVICE_KEY,
            },
            body: JSON.stringify({
              email,
              name: user.name,
              avatar: user.image,
              socialType: 'vk',
              socialId: vkId,
            }),
          });

          if (res.ok) {
            const data = await res.json();
            console.log('VK service-login success:', data);

            user.id = data.user?.id || data.user?.email || user.id;
            user.email = data.user?.email || user.email;
            user.name = data.user?.name || user.name;
            user.accessToken = data.accessToken || data.token;

            return true;
          } else {
            const errorData = await res.json();
            console.error('VK service-login failed:', errorData);
            return false;
          }
        } catch (error) {
          console.error('VK signIn error:', error);
          return false;
        }
      }
      if (account.provider === 'yandex') {
        console.log('yandex', user, account, profile);
        const email = user.email;
        const avatar = user.image;
        let name = profile.login;
        // Если это email, обрезаем до символа "@"
        if (typeof name === 'string' && name.includes('@')) {
          name = name.split('@')[0];
        }
        const id = profile.id;
        console.log('service-key', PAYLOAD_SERVICE_KEY);
        console.log({
          email,
          name,
          avatar,
          socialType: 'yandex',
          socialId: id,
        });
        try {
          const res = await fetch(`${API_BASE_URL}/api/users/service-login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'service-key': PAYLOAD_SERVICE_KEY,
            },
            body: JSON.stringify({
              email,
              name,
              avatar,
              socialType: 'yandex',
              socialId: id,
            }),
          });
          if (res.ok) {
            const data = await res.json();
            user.id = data.user?.id || data.user?.email || user.id;
            user.email = data.user?.email || user.email;
            user.name = data.user?.name || user.name;
            user.accessToken = data.accessToken || data.token;
            user.isNewUser = data.user?.createdAt === data.user?.updatedAt;
            // Добавьте другие нужные поля, если есть
            return true;
          }
          console.log(await res.json(), 'res');
          return false;
        } catch (error) {
          console.error('Yandex signIn error:', error);
          return false;
        }
      }
      return true;
    },
  },
  pages: {
    signIn: '/', // Используем модальное окно вместо отдельной страницы
  },
  session: {
    strategy: 'jwt',
    maxAge: 180 * 24 * 60 * 60, // 180 дней
    updateAge: 24 * 60 * 60, // Обновляем каждый день
  },
  jwt: {
    maxAge: 180 * 24 * 60 * 60, // 180 дней
  },
  secret: process.env.NEXTAUTH_SECRET,
  debug: process.env.NODE_ENV === 'development',
  useSecureCookies: process.env.NODE_ENV === 'production',
  cookies: {
    sessionToken: {
      name: `next-auth.session-token`,
      options: {
        httpOnly: true,
        sameSite: 'lax',
        path: '/',
        secure: process.env.NODE_ENV === 'production',
      },
    },
  },
};
